// components/dialog/LoginDialog.tsx
import React, { useState, useEffect } from 'react';
import { inAppWallet } from 'thirdweb/wallets';
import { useConnect } from 'thirdweb/react';
import { client } from '../../utils/client';
import { polygon } from 'thirdweb/chains';
import { closeModal } from '../../store/Login/modalSlice';
import Image from 'next/image';
import MainButton from '../buttons/mainButton';
import { useAppDispatch } from '../../store/store';
import { addAlert } from '../../store/alert/alertSlice';
import { AlertStatus } from '../../utils/alert.type';
import { addLoading } from '../../store/loading/loadingSlice';
import {logEvent} from '../../utils/analytics';

import {
    getTokenFromServer, resetPasswordRequest,
    sendRegisterRequest,
    sendVerifyCodeRequest,
    tryPasswordResetRequest,
    verifyAccessToken
} from '../../api/ApiAuth';
import {LoginDialogProps} from '../../api/ApiResponse';
import {getLocalizedMessage} from '../../utils/localization';
type RegistrationPhase = 'form' | 'code';

const LoginDialog: React.FC<LoginDialogProps> = ({ prefilledReferral }) => {
    const dispatch = useAppDispatch();
    const { connect } = useConnect();

    const [activeTab, setActiveTab] = useState<'login' | 'register'>(
        prefilledReferral ? 'register' : 'login'
    );

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerRepeat, setRegisterRepeat] = useState('');
    const [registerReferral, setRegisterReferral] = useState(prefilledReferral || '');
    const [registrationCode, setRegistrationCode] = useState('');
    const [registrationPhase, setRegistrationPhase] = useState<RegistrationPhase>('form');

    const [message, setMessage] = useState('');
    const [isSuccessMessage, setIsSuccessMessage] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [utmSource, setUtmSource] = useState<string | null>(null);
    const [utmMedium, setUtmMedium] = useState<string | null>(null);
    const [utmCampaign, setUtmCampaign] = useState<string | null>(null);
    const [utmUserId, setUtmUserId] = useState<string | null>(null);

    const [isResetPasswordMode, setIsResetPasswordMode] = useState(false);
    const [resetPasswordEmail, setResetPasswordEmail] = useState('');
    const [resetPasswordCode, setResetPasswordCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [resetPasswordPhase, setResetPasswordPhase] = useState<'email' | 'reset'>('email');

    useEffect(() => {
        if (prefilledReferral) {
            setActiveTab('register');
            setRegisterReferral(prefilledReferral);
        }
    }, [prefilledReferral]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const source = searchParams.get('utm_source');
        const medium = searchParams.get('utm_medium');
        const campaign = searchParams.get('utm_campaign');
        const userId = searchParams.get('user_id');

        setUtmSource(source);
        setUtmMedium(medium);
        setUtmCampaign(campaign);
        setUtmUserId(userId);
    }, []);

    const handleLogin = async () => {

        if (email.trim() === '' || password.trim() === '') {
            setIsSuccessMessage(false);
            setMessage('Please fill in the Email and Password fields.');
            return;
        }

        setIsLoading(true);
        dispatch(addLoading(true));
        setMessage('');
        try {
            const tokenResponse = await getTokenFromServer({email, password});

            if (tokenResponse.error) {
                setIsSuccessMessage(false);
                setMessage(getLocalizedMessage(tokenResponse.error));
                dispatch(addLoading(false))
                return;
            }

            if(!tokenResponse.payload) return;

            localStorage.setItem('access_token', tokenResponse.payload.accessToken);
            localStorage.setItem('refresh_token', tokenResponse.payload.refreshToken);

            const verifyTokenResponse = await verifyAccessToken({accessToken: tokenResponse.payload.accessToken});

            if (verifyTokenResponse.error) {
                setMessage(getLocalizedMessage(verifyTokenResponse.error));
                dispatch(addLoading(false))
                return;
            }

            if (verifyTokenResponse.payload) {
                logEvent('User', 'Login', 'LoginForm');
                await connectWithPayload(verifyTokenResponse.payload.accessToken);
                dispatch(closeModal());
            }

        } catch (error) {
            setIsSuccessMessage(false);
            setMessage('Something went wrong, please try again');
            dispatch(addLoading(false));
            dispatch(
                addAlert({
                    title: 'Important notification',
                    message: 'Something went wrong, please try again',
                    type: AlertStatus.error,
                })
            );
        } finally {
            setIsLoading(false);
        }
    };
    const handleSendCode = async () => {
        if ( registerEmail.trim() === '' ||  registerPassword.trim() === '' || registerRepeat.trim() === '' ) {
            setIsSuccessMessage(false);
            setMessage('Please fill in all required fields.');
            return;
        }

        if (registerPassword !== registerRepeat) {
            setIsSuccessMessage(false);
            setMessage('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        dispatch(addLoading(true));
        setMessage('');

        try {
            const response = await sendRegisterRequest({
                email: registerEmail,
                password: registerPassword,
                referrerId: registerReferral || null,
                utmSource,
                utmMedium,
                utmCampaign,
                utmUserId,
            });

            if (response.error) {
                setIsSuccessMessage(false);
                setMessage(getLocalizedMessage(response.error));
                return;
            }

            if (response.success) {
                setIsSuccessMessage(true);
                setMessage(getLocalizedMessage(response.success));
                setRegistrationPhase('code');
                return;
            }

            if (response.message) {
                setIsSuccessMessage(false);
                setMessage(response.message);
            }
        } catch (error) {
            setIsSuccessMessage(false);
            setMessage('Registration failed');
        } finally {
            setIsLoading(false);
            dispatch(addLoading(false));
        }
    };
    const handleRegistration = async () => {
        if (registrationCode.trim() === '') {
            setIsSuccessMessage(false);
            setMessage('Please enter the verification code.');
            return;
        }

        setIsLoading(true);
        dispatch(addLoading(true));
        setMessage('');

        try {
            const response = await sendVerifyCodeRequest({
                email: registerEmail,
                password: registerPassword,
                code: registrationCode,
            });

            if (response.error) {
                setIsSuccessMessage(false);
                setMessage(getLocalizedMessage(response.error));
                return;
            }

            if (response.success) {
                setIsSuccessMessage(true);
                setMessage(getLocalizedMessage(response.success));
                setEmail(registerEmail);
                setPassword(registerPassword);
                setActiveTab('login');
                logEvent('User', 'Registration', 'RegistrationForm');
                return;
            }

            if (response.message) {
                setIsSuccessMessage(false);
                setMessage(response.message);
            }
        } catch (error) {
            setIsSuccessMessage(false);
            setMessage('Verification failed');
        } finally {
            setIsLoading(false);
            dispatch(addLoading(false));
        }
    };
    const handleRequestPasswordReset = async () => {
        if (!resetPasswordEmail.trim()) {
            setIsSuccessMessage(false);
            setMessage('Please enter your email');
            return;
        }

        setIsLoading(true);
        try {
            const response = await tryPasswordResetRequest({ email: resetPasswordEmail });

            if (response.error) {
                setIsSuccessMessage(false);
                setMessage(getLocalizedMessage(response.error));
                return;
            }

            if (response.success) {
                setIsSuccessMessage(true);
                setMessage(getLocalizedMessage(response.success));
                setResetPasswordPhase('reset');
                return;
            }

            if (response.message) {
                setIsSuccessMessage(false);
                setMessage(response.message);
            }
        } catch (error) {
            setIsSuccessMessage(false);
            setMessage('Failed to send reset code');
        } finally {
            setIsLoading(false);
        }
    };
    const handleResetPassword = async () => {
        if (!resetPasswordCode.trim() || !newPassword.trim() || !confirmNewPassword.trim()) {
            setIsSuccessMessage(false);
            setMessage('Please fill all fields');
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setIsSuccessMessage(false);
            setMessage('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            const response = await resetPasswordRequest({
                email: resetPasswordEmail,
                password: newPassword,
                code: resetPasswordCode
            });

            if (response.error) {
                setIsSuccessMessage(false);
                setMessage(getLocalizedMessage(response.error));
                return;
            }

            if (response.success) {
                setIsSuccessMessage(true);
                setMessage(getLocalizedMessage(response.success));
                setIsResetPasswordMode(false);
                setResetPasswordPhase('email');
                setActiveTab('login');
                return;
            }

            if (response.message) {
                setIsSuccessMessage(false);
                setMessage(response.message);
            }
        } catch (error) {
            setIsSuccessMessage(false);
            setMessage('Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };
    const connectWithPayload = async (payload: string) => {
        try {
            await connect(async () => {
                const walletInstance = inAppWallet();
                await walletInstance.connect({
                    client,
                    strategy: 'auth_endpoint',
                    payload: payload,
                    chain: polygon,
                });
                return walletInstance;
            });
            dispatch(addLoading(false));
        } catch (error) {
            setIsSuccessMessage(false);
            setMessage('Failed to connect wallet');
        }
    };

    return (
      <div className="fixed inset-0 z-[50] flex items-center justify-center">
        {/* Overlay */}
        <div className="absolute inset-0 bg-black opacity-50" onClick={() => dispatch(closeModal())} />
        {/* Modal container */}
        <div className="relative rounded-lg shadow-lg p-8 mx-4 max-w-[1200px] max-h-[700px] w-full h-full">
          {/* Background image */}
          <Image
            src="/login/NewLoginPanel.webp"
            alt="Login Background"
            fill
            className="object-cover object-center rounded-lg"
            priority
          />

          <div className="relative z-10 mt-40 ml-6">
            <div className="flex justify-center mb-4">
              <button onClick={() => setActiveTab('login')} className="flex-shrink-0 relative">
                <Image
                    src={activeTab === 'login' ? '/login/LoginTabActive.webp' : '/login/LoginTabDisabled.webp'}
                    alt="Login Tab"
                    width={120}
                    height={50}
                    className="rounded"
                />
                <span className="absolute inset-0 flex items-center justify-center text-lg font-georgia font-[700] text-white select-none ml-4">
                  Login
                </span>
              </button>
              <button onClick={() => setActiveTab('register')} className="flex-shrink-0 relative ml-0">
                <Image
                    src={activeTab === 'register' ? '/login/RegistrationTabActive.webp' : '/login/RegistrationTabDisabled.webp'}
                    alt="Register Tab"
                    width={120}
                    height={50}
                    className="rounded"
                 />
                <span className="absolute inset-0 flex items-center justify-center text-lg font-georgia font-[700] text-white select-none mr-4">
                  Register
                </span>
              </button>
            </div>

            {activeTab === 'login' && !isResetPasswordMode && (
            <div className="flex flex-col items-center space-y-3">
              <input
            type="email"
            placeholder="Email"
            className="w-[250px] p-2 border rounded"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
        />
              <input
            type="password"
            placeholder="Password"
            className="w-[250px] p-2 border rounded"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
        />
              <button
            onClick={() => setIsResetPasswordMode(true)}
            className="text-white text-left w-full max-w-[250px] hover:underline"
        >
                Forgot Password?
              </button>
              <div className="flex flex-col items-center space-y-2">
                <MainButton
                borderColor="white"
                stopColorOne="#006400"
                stopColorTwo="#228B22"
                className="w-[250px] px-4 py-2 text-white rounded text-[18px]"
                onClick={handleLogin}
            >
                  {isLoading ? 'Connecting...' : 'Login'}
                </MainButton>
                {message && (
                <p className={`text-sm font-medium text-center w-[250px] break-words ${
                    isSuccessMessage ? 'text-green-500' : 'text-red-500'
                }`}>
                  {message}
                </p>
            )}
              </div>
            </div>
)}

            {activeTab === 'login' && isResetPasswordMode && (
            <div className="flex flex-col items-center space-y-3">
              {resetPasswordPhase === 'email' ? (
                <>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-[250px] p-2 border rounded"
                    value={resetPasswordEmail}
                    onChange={(e) => setResetPasswordEmail(e.target.value)}
                />
                  <div className="flex flex-col items-center space-y-2">
                    <MainButton
                        borderColor="white"
                        stopColorOne="#006400"
                        stopColorTwo="#228B22"
                        className="w-[250px] px-4 py-2 text-white rounded text-[18px]"
                        onClick={handleRequestPasswordReset}
                    >
                      Send Code
                    </MainButton>
                  </div>
                </>
        ) : (
          <>
            <input
                    type="text"
                    placeholder="Enter reset code"
                    className="w-[250px] p-2 border rounded"
                    value={resetPasswordCode}
                    onChange={(e) => setResetPasswordCode(e.target.value)}
                />
            <input
                    type="password"
                    placeholder="New password"
                    className="w-[250px] p-2 border rounded"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                />
            <input
                    type="password"
                    placeholder="Confirm new password"
                    className="w-[250px] p-2 border rounded"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
            <div className="flex flex-col items-center space-y-2">
              <MainButton
                        borderColor="white"
                        stopColorOne="#006400"
                        stopColorTwo="#228B22"
                        className="w-[250px] px-4 py-2 text-white rounded text-[18px]"
                        onClick={handleResetPassword}
                    >
                Reset Password
              </MainButton>
            </div>
          </>
        )}
              {message && (
              <p className={`text-sm font-medium text-center w-[250px] break-words ${
                isSuccessMessage ? 'text-green-500' : 'text-red-500'
            }`}>
                {message}
              </p>
        )}
              <button
            onClick={() => {
                setIsResetPasswordMode(false);
                setResetPasswordPhase('email');
                setMessage('');
            }}
            className="text-white hover:underline"
        >
                Back to Login
              </button>
            </div>
)}

            {activeTab === 'register' && (
            <div className="flex flex-col items-center space-y-3">
              {registrationPhase === 'form' ? (
                <>
                  <input
                    type="email"
                    placeholder="Email*"
                    className="w-[250px] p-2 border rounded"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="Password*"
                    className="w-[250px] p-2 border rounded"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="Repeat Password*"
                    className="w-[250px] p-2 border rounded"
                    value={registerRepeat}
                    onChange={(e) => setRegisterRepeat(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Referral ID"
                    className={`w-[250px] p-2 border rounded ${prefilledReferral ? 'bg-gray-400 text-gray-700 cursor-not-allowed' : ''}`}
                    value={registerReferral}
                    onChange={(e) => setRegisterReferral(e.target.value)}
                    readOnly={!!prefilledReferral}
                  />
                  <div className="flex flex-col items-center space-y-2">
                    <MainButton
                      borderColor="white"
                      stopColorOne="#006400"
                      stopColorTwo="#228B22"
                      className="w-[250px] px-4 py-2 text-white rounded text-[18px]"
                      onClick={handleSendCode}
                    >
                      Send code
                    </MainButton>
                    {message && registrationPhase === 'form' && (
                      <p
                        className={`text-sm font-medium text-center w-[250px] break-words ${isSuccessMessage ? 'text-green-500' : 'text-red-500'}`}
                      >
                        {message}
                      </p>
                    )}
                  </div>
                </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Enter verification code"
                      className="w-[250px] p-2 border rounded"
                      value={registrationCode}
                      onChange={(e) => setRegistrationCode(e.target.value)}
                    />
                    <div className="flex flex-col items-center space-y-2">
                      <MainButton
                        borderColor="white"
                        stopColorOne="#006400"
                        stopColorTwo="#228B22"
                        className="w-[250px] px-4 py-2 text-white rounded text-[18px]"
                        onClick={handleRegistration}
                      >
                        Registration
                      </MainButton>
                      {message && registrationPhase === 'code' && (
                      <p
                          className={`text-sm font-medium text-center w-[250px] break-words ${isSuccessMessage ? 'text-green-500' : 'text-red-500'}`}
                        >
                        {message}
                      </p>
                      )}
                    </div>
                  </>
                )}
            </div>
                    )}

          </div>
        </div>
      </div>
    );
};

export default LoginDialog;